import { RESOURCE_TYPES } from "./resources.ts";

/** Onde a receita é feita. Mãos, Bancada e Bigorna são instantâneas; Forno e Panela processam (F16). */
export type Station = "maos" | "bancada" | "bigorna" | "forno" | "panela";

/** Item que, posicionado no mundo, oferece a estação. */
export const STATION_ITEMS: Record<Exclude<Station, "maos">, string> = {
  bancada: "bancada",
  bigorna: "bigorna",
  forno: "forno-barro",
  panela: "panela-barro",
};
export const STATION_NAMES: Record<Station, string> = {
  maos: "Mãos",
  bancada: "Bancada",
  bigorna: "Bigorna",
  forno: "Forno de Barro",
  panela: "Panela de Barro",
};
/** Raio em que a estação vale para fabricar (doc 03). */
export const STATION_RADIUS = 4;

export interface Ingredient {
  item: string;
  qty: number;
}

export interface Recipe {
  /** Id da receita = id do item produzido (uma receita por item). */
  id: string;
  output: string;
  qty: number;
  station: Station;
  inputs: readonly Ingredient[];
  /** Tempo de processo (Forno/Panela), em segundos. */
  seconds?: number;
  /** Forno: exige Carvão como combustível. */
  coalOnly?: boolean;
  /** Descoberta só pelo ingrediente-chave (padrão: todos os ingredientes vistos). */
  key?: string;
}

type Inputs = Record<string, number>;
const r = (output: string, station: Station, inputs: Inputs, qty = 1, extra: Partial<Recipe> = {}): Recipe => ({
  id: output,
  output,
  qty,
  station,
  inputs: Object.entries(inputs).map(([item, n]) => ({ item, qty: n })),
  ...extra,
});

/** Receitas do catálogo (doc 03). Números são hipóteses de balanceamento (doc 07). */
export const RECIPES: readonly Recipe[] = [
  // ---- Mãos ----
  r("corda", "maos", { palha: 3 }),
  r("machado-madeira", "maos", { madeira: 6, corda: 2 }),
  r("picareta-madeira", "maos", { madeira: 6, corda: 2 }),
  r("bancada", "maos", { madeira: 15, corda: 2 }),

  // ---- Bancada: ferramentas e armas ----
  r("machado-pedra", "bancada", { madeira: 5, pedra: 8, corda: 2 }),
  r("picareta-pedra", "bancada", { madeira: 5, pedra: 8, corda: 2 }),
  r("machado-ferro", "bancada", { "madeira-de-lei": 4, "lingote-ferro": 6, corda: 1 }),
  r("picareta-ferro", "bancada", { "madeira-de-lei": 4, "lingote-ferro": 6, corda: 1 }),
  r("porrete", "bancada", { madeira: 6, corda: 1 }),
  r("lanca-pau", "bancada", { madeira: 5, corda: 1 }),
  r("cajado-galho", "bancada", { madeira: 4, corda: 1, pederneira: 1 }),
  r("arco-galho", "bancada", { madeira: 6, corda: 2 }),
  r("facao-pederneira", "bancada", { madeira: 3, pederneira: 4, corda: 1 }),
  r("arco-ipe", "bancada", { "madeira-de-lei": 6, corda: 2 }),
  r("facao-ferro", "bancada", { "madeira-de-lei": 2, "lingote-ferro": 6, couro: 1 }),
  r("lanca-ferro", "bancada", { "madeira-de-lei": 4, "lingote-ferro": 4 }),
  r("cajado-ferro", "bancada", { "madeira-de-lei": 4, "lingote-ferro": 3 }),
  r("marreta-ferro", "bancada", { "madeira-de-lei": 4, "lingote-ferro": 10 }),
  r("flecha-pau", "bancada", { madeira: 1, palha: 1 }, 6),
  r("flecha-pederneira", "bancada", { madeira: 1, pederneira: 1 }, 6),
  r("flecha-ferro", "bancada", { "madeira-de-lei": 1, "lingote-ferro": 1 }, 8),

  // ---- Bancada: armaduras ----
  r("chapeu-palha", "bancada", { palha: 6, corda: 1 }),
  r("colete-talas", "bancada", { madeira: 8, corda: 2 }),
  r("perneiras-talas", "bancada", { madeira: 6, corda: 2 }),
  r("alpercatas-palha", "bancada", { palha: 4, corda: 1 }),
  r("capacete-couro", "bancada", { couro: 3, corda: 1 }),
  r("gibao-couro", "bancada", { couro: 7, corda: 2 }),
  r("perneiras-couro", "bancada", { couro: 5, corda: 1 }),
  r("botas-vaqueiro", "bancada", { couro: 2, corda: 1 }),
  r("capacete-ferro", "bancada", { "lingote-ferro": 4, couro: 1 }),
  r("peitoral-ferro", "bancada", { "lingote-ferro": 9, couro: 2 }),
  r("grevas-ferro", "bancada", { "lingote-ferro": 7, couro: 1 }),
  r("botas-ferro", "bancada", { "lingote-ferro": 3, couro: 1 }),

  // ---- Bancada: construção, estações e utilitários ----
  r("alicerce-pedra", "bancada", { pedra: 4, barro: 1 }),
  r("piso-tabuas", "bancada", { madeira: 2 }),
  r("parede-tabuas", "bancada", { madeira: 3 }),
  r("parede-taipa", "bancada", { madeira: 2, barro: 4 }),
  r("parede-porta", "bancada", { madeira: 4, corda: 1 }),
  r("rampa-tabuas", "bancada", { madeira: 3 }),
  r("telhado-palha", "bancada", { madeira: 1, palha: 4 }),
  r("estacada", "bancada", { madeira: 4, corda: 1 }),
  r("forno-barro", "bancada", { barro: 20, pedra: 8 }),
  r("panela-barro", "bancada", { barro: 10, pedra: 4, madeira: 2 }),
  r("bigorna", "bancada", { "lingote-ferro": 12, pedra: 15 }),
  r("bau-madeira", "bancada", { madeira: 18, corda: 2 }),
  r("peneira", "bancada", { madeira: 4, corda: 3 }),
  r("tocha", "bancada", { madeira: 1, carvao: 1 }, 4),

  // ---- Bigorna: Prata e Pedra-de-raio ----
  r("machado-prata", "bigorna", { "madeira-de-lei": 4, "lingote-prata": 6, "resina-jatoba": 2 }),
  r("picareta-prata", "bigorna", { "madeira-de-lei": 4, "lingote-prata": 6, "resina-jatoba": 2 }),
  r("espada-prata", "bigorna", { "madeira-de-lei": 2, "lingote-prata": 7, "resina-jatoba": 1 }),
  r("lanca-prata", "bigorna", { "madeira-de-lei": 4, "lingote-prata": 5, "resina-jatoba": 1 }),
  r("cajado-prata", "bigorna", { "madeira-de-lei": 4, "lingote-prata": 4, "resina-jatoba": 2 }),
  r("arco-prata", "bigorna", { "madeira-de-lei": 5, "lingote-prata": 4, "resina-jatoba": 2, corda: 2 }),
  r("flecha-prata", "bigorna", { "madeira-de-lei": 1, "lingote-prata": 1 }, 8),
  r("elmo-prata", "bigorna", { "lingote-prata": 4, couro: 1 }),
  r("peitoral-prata", "bigorna", { "lingote-prata": 9, couro: 2 }),
  r("grevas-prata", "bigorna", { "lingote-prata": 7, couro: 1 }),
  r("botas-prata", "bigorna", { "lingote-prata": 3, couro: 1 }),
  r("machado-raio", "bigorna", { "machado-prata": 1, "lingote-raio": 2, "tora-angelim": 1 }, 1, { key: "lingote-raio" }),
  r("picareta-raio", "bigorna", { "picareta-prata": 1, "lingote-raio": 2, "tora-angelim": 1 }, 1, { key: "lingote-raio" }),
  r("espada-raio", "bigorna", { "espada-prata": 1, "lingote-raio": 3, "tora-angelim": 1 }, 1, { key: "lingote-raio" }),
  r("marreta-trovejante", "bigorna", { "marreta-ferro": 1, "lingote-raio": 4, "tora-angelim": 2 }, 1, { key: "lingote-raio" }),
  r("arco-trovao", "bigorna", { "arco-prata": 1, "lingote-raio": 3, "tora-angelim": 2 }, 1, { key: "lingote-raio" }),
  r("elmo-raio", "bigorna", { "elmo-prata": 1, "lingote-raio": 1 }, 1, { key: "lingote-raio" }),
  r("couraca-raio", "bigorna", { "peitoral-prata": 1, "lingote-raio": 3 }, 1, { key: "lingote-raio" }),
  r("grevas-raio", "bigorna", { "grevas-prata": 1, "lingote-raio": 2 }, 1, { key: "lingote-raio" }),
  r("botas-raio", "bigorna", { "botas-prata": 1, "lingote-raio": 1 }, 1, { key: "lingote-raio" }),

  // ---- Forno (processamento na F16) ----
  r("lingote-ferro", "forno", { "minerio-ferro": 1 }, 1, { seconds: 8 }),
  r("lingote-prata", "forno", { "minerio-prata": 1 }, 1, { seconds: 12 }),
  r("lingote-raio", "forno", { "fragmento-raio": 2 }, 1, { seconds: 20, coalOnly: true }),
  r("carvao", "forno", { madeira: 2 }, 1, { seconds: 6 }),
  r("carne-assada", "forno", { "carne-crua": 1 }, 1, { seconds: 6 }),
  r("peixe-assado", "forno", { "peixe-cru": 1 }, 1, { seconds: 6 }),
  r("tapioca", "forno", { mandioca: 2 }, 1, { seconds: 8 }),

  // ---- Panela (processamento na F16) ----
  r("mandioca-cozida", "panela", { mandioca: 2 }, 1, { seconds: 8 }),
  r("moqueca", "panela", { "peixe-cru": 2, coco: 1 }, 1, { seconds: 14 }),
  r("baiao", "panela", { feijao: 2, "carne-crua": 1, mandioca: 1 }, 1, { seconds: 14 }),
  r("acai-tigela", "panela", { acai: 3, banana: 1 }, 1, { seconds: 6 }),
  r("pamonha", "panela", { milho: 2, coco: 1, palha: 1 }, 1, { seconds: 12 }),
];

export const RECIPE_BY_ID = new Map(RECIPES.map((recipe) => [recipe.id, recipe]));

/**
 * Itens que surgem fora de receitas: drops de nós de coleta, fauna (F13), roças selvagens (F20),
 * chefes e guardiões (F22–F23) e itens-chave achados no mundo.
 */
export const NATURAL_ITEMS: ReadonlySet<string> = new Set([
  ...RESOURCE_TYPES.flatMap((t) => t.drops.map((d) => d.item)),
  "carne-crua",
  "couro",
  "mandioca",
  "milho",
  "feijao",
  "fragmento-raio",
  // Tostão: baús, assombrações e o mascate (F18/F19).
  "tostao",
  "mapa-jangada",
  "mapa-muiraquitas",
  "muiraquita-verde",
  "muiraquita-azul",
  "muiraquita-vermelho",
  "muiraquita-amarelo",
  "muiraquita-rosa",
]);

/** Receitas feitas na hora (sem processamento). */
export const isInstant = (recipe: Recipe): boolean => recipe.station === "maos" || recipe.station === "bancada" || recipe.station === "bigorna";
